import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';

const CalendarGrid = ({ 
    calendarRows,      
    weekendsVisible,   
    events,            
    expandedDates: parentExpandedDates, 
    user,              
    handleDateClick,   
    handleEventClick,  
    handleToggleExpand,
    isCalFull 
}) => {
    const activeCurrentMonth = (() => {
        if (calendarRows && calendarRows.length > 2) {
            const middleWeek = calendarRows[2];
            const middleDay = middleWeek[0]?.date || middleWeek[0];
            return dayjs(middleDay).month();
        }
        return dayjs().month();
    })();

    const [localExpandedDates, setLocalExpandedDates] = useState([]);
    const expandedDates = parentExpandedDates && parentExpandedDates.length > 0 ? parentExpandedDates : localExpandedDates;
    const maxVisibleCount = 3; 

    // 더보기 / 접기 토글 핸들러
    const toggleExpand = (dateStr, e) => {
        e.stopPropagation();
        setLocalExpandedDates(prev =>
            prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
        );
        if (handleToggleExpand) handleToggleExpand(dateStr, e);
    };
    return (
        <Box sx={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', display: 'flex', flexDirection: 'column', gap: '1px', width: '100%' }}>
            {calendarRows.map((week, weekIdx) => {
                const weekStart = dayjs(week[0]?.date || week[0]).startOf('day');
                const weekEnd = dayjs(week[week.length - 1]?.date || week[week.length - 1]).endOf('day');
                
                const matchedEvents = (events || []).filter(event => {
                    if (!event.start || !event.end) return false;
                    const eStart = dayjs(event.start.substring(0, 10)).startOf('day');
                    const eEnd = dayjs(event.end.substring(0, 10)).endOf('day');
                    return (eStart.isBefore(weekEnd) || eStart.isSame(weekEnd, 'day')) && 
                           (eEnd.isAfter(weekStart) || eEnd.isSame(weekStart, 'day'));
                }).sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

                const timelineTracks = [];
                const renderedWeekEvents = [];
                
                matchedEvents.forEach(event => {
                    const eStart = dayjs(event.start.substring(0, 10)).startOf('day');
                    const eEnd = dayjs(event.end.substring(0, 10)).endOf('day');
                    const startIdx = Math.max(0, eStart.isBefore(weekStart) ? 0 : eStart.day());
                    const endIdx = Math.min(week.length - 1, eEnd.isAfter(weekEnd) ? week.length - 1 : eEnd.day());

                    let adjustedStart = startIdx;
                    let adjustedEnd = endIdx;
                    if (!weekendsVisible) {
                        if (startIdx === 0) adjustedStart = 1; 
                        if (endIdx === 6) adjustedEnd = 5;     
                        adjustedStart -= 1; adjustedEnd -= 1;
                        if (adjustedStart > 4 || adjustedEnd < 0) return; 
                    }
                    const colSpan = adjustedEnd - adjustedStart + 1;

                    let targetTrackIndex = -1;
                    for (let i = 0; i < timelineTracks.length; i++) {
                        const hasOverlap = timelineTracks[i].some(allocated => !(adjustedEnd < allocated.startIdx || adjustedStart > allocated.endIdx));
                        if (!hasOverlap) { targetTrackIndex = i; break; }
                    }
                    if (targetTrackIndex === -1) {
                        timelineTracks.push([{ startIdx: adjustedStart, endIdx: adjustedEnd }]);
                        targetTrackIndex = timelineTracks.length - 1;
                    } else {
                        timelineTracks[targetTrackIndex].push({ startIdx: adjustedStart, endIdx: adjustedEnd });
                    }
                    renderedWeekEvents.push({ ...event, startIdx: adjustedStart, colSpan: colSpan, topLevel: targetTrackIndex });
                });

                const isWeekRowExpanded = week.some(dayCell => expandedDates.includes(dayjs(dayCell.date || dayCell).format('YYYY-MM-DD')));
                const totalTracksCount = timelineTracks.length;
                const currentVisibleTracksCount = isWeekRowExpanded ? totalTracksCount : Math.min(maxVisibleCount, totalTracksCount);
                const containerHeight = 40 + (currentVisibleTracksCount * (typeof window !== 'undefined' && window.innerWidth <= 480 ? 24 : 28)) + (totalTracksCount > maxVisibleCount ? (typeof window !== 'undefined' && window.innerWidth <= 480 ? 4 : 24) : 10);
                return (
                    <Box 
                        key={weekIdx} 
                        sx={{ 
                            position: 'relative', 
                            backgroundColor: '#ffffff', 
                            display: 'grid', 
                            gridTemplateColumns: `repeat(${weekendsVisible ? 7 : 5}, 1fr)`, 
                            gap: '1px', 
                            
                            // 🚀 [수정 핵심]: 확장 상태(isCalFull)일 때는 높이 제한을 'auto'로 풀고 내용물이 탈출할 수 있게 'visible' 처리를 해줍니다.
                            // 축소 상태일 때는 원래 원본대로 고정 높이와 넘치는 데이터를 깎아내는 'hidden'으로 돌아갑니다.
                            minHeight: isCalFull ? 'auto' : `${Math.max(135, containerHeight)}px`, 
                            overflow: isCalFull ? 'visible' : 'hidden' 
                        }}
                    >
                        {week.map((dayCell, dayIdx) => {
                            const dayDate = dayjs(dayCell.date || dayCell);
                            const dayOfWeek = dayDate.day();
                            if (!weekendsVisible && (dayOfWeek === 0 || dayOfWeek === 6)) return null;

                            const isCurrentMonth = dayDate.month() === activeCurrentMonth;
                            const isToday = dayjs().isSame(dayDate, 'day');
                            const formattedDate = dayDate.format('YYYY-MM-DD');

                            const dayAllEvents = renderedWeekEvents.filter(event => {
                                const eStartStr = event.start.substring(0, 10);
                                const eEndStr = event.end.substring(0, 10);
                                return formattedDate >= eStartStr && formattedDate <= eEndStr;
                            });

                            const hasHiddenTracks = dayAllEvents.some(e => e.topLevel >= maxVisibleCount);
                            const isExpanded = expandedDates.includes(formattedDate);
                            const maxTrackInWeek = week.reduce((max, dayCell) => {
                                const dDate = dayjs(dayCell.date || dayCell).format('YYYY-MM-DD');
                                const dEvents = renderedWeekEvents.filter(e => {
                                    const s = e.start.substring(0, 10);
                                    const ed = e.end.substring(0, 10);
                                    return dDate >= s && dDate <= ed;
                                });
                                const maxTopLevel = dEvents.length > 0 ? Math.max(...dEvents.map(e => e.topLevel || 0)) : 0;
                                return maxTopLevel > max ? maxTopLevel : max;
                            }, 0);

                            const dynamicFullHeight = 40 + ((maxTrackInWeek + 1) * 28) + 16;
                            const finalMinHeight = Math.max(120, dynamicFullHeight);
                            const isSalesPath = window.location.pathname === '/salesActivity';

                            // 🚀 [추가 핵심 1]: 확장 상태(isCalFull)이거나, 또는 세일즈 메뉴 주소(isSalesPath)일 때 전체 노출을 활성화합니다.
                            const shouldExpandAll = isCalFull || isSalesPath;

                            return (
                                <Box 
                                    key={dayIdx} 
                                    onClick={() => handleDateClick && handleDateClick(formattedDate)} 
                                    sx={{ 
                                        backgroundColor: isCurrentMonth ? (isToday ? 'rgba(25, 118, 210, 0.04)' : '#ffffff') : '#fafafa', 
                                        p: 1, 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        boxSizing: 'border-box', 
                                        borderRight: dayIdx < 6 ? '1px solid #e0e0e0' : 'none', 
                                        position: 'relative', 
                                        zIndex: 1, 
                                        cursor: 'pointer',

                                        // 🚀 [추가 핵심 2]: 
                                        // 확장 상태이거나 세일즈 메뉴일 때는 계산된 수치(finalMinHeight px)를 대입해 자동으로 칸을 늘려줍니다.
                                        // 정렬을 위해 height: '100%' 구조는 그대로 유지하여 가로 격자가 깨지는 현상을 완벽히 방지합니다.
                                        height: '100%', 
                                        minHeight: shouldExpandAll ? `${finalMinHeight}px` : '120px', 
                                        
                                        // 🚀 [추가 핵심 3]:
                                        // 펼쳐진 상태일 때는 가려진 일정이 뚫려 보이도록 'visible'로 풀고, 축소/타메뉴 상태일 때는 다시 'hidden'으로 단정하게 자릅니다.
                                        overflow: shouldExpandAll ? 'visible' : 'hidden',
                                        pb: shouldExpandAll ? '24px' : '8px' 
                                    }}
                                >
                                    <Typography 
                                        variant="body1" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            mb: 1, 
                                            opacity: isCurrentMonth ? 1 : 0.4, 
                                            color: dayOfWeek === 0 ? '#dc3545' : dayOfWeek === 6 ? '#0d6efd' : '#333333', 
                                            fontSize: '12px' 
                                        }}
                                    >
                                        {dayDate.date()}
                                    </Typography>

                                    {/* 🚀 [추가 핵심 4]: 평소 대시보드 축소 상태(shouldExpandAll이 아닐 때)이면서 숨겨진 일정이 있을 때만 '+ 더보기' 단추를 화면에 출력합니다. */}
                                    {hasHiddenTracks && !shouldExpandAll && (
                                        <Typography 
                                            onClick={(e) => toggleExpand(formattedDate, e)} 
                                            variant="caption" 
                                            sx={{ 
                                                position: 'absolute', 
                                                bottom: '6px', 
                                                right: '8px', 
                                                color: isExpanded ? '#dc3545' : '#0052cc', 
                                                fontWeight: 'bold', 
                                                cursor: 'pointer', 
                                                zIndex: 5, 
                                                fontSize: '12px !important',
                                                backgroundColor: isCurrentMonth ? (isToday ? 'rgba(25, 118, 210, 0.04)' : '#ffffff') : '#fafafa', 
                                                pl: '4px', 
                                                '&:hover': { textDecoration: 'underline' },

                                                '@media (max-width: 480px)': {
                                                    fontSize: '8px !important',
                                                    bottom: '3px !important',
                                                    right: '4px !important'
                                                }
                                            }}
                                        >
                                            {isExpanded ? '접기 ▲' : `+ 더보기 ▼`}
                                        </Typography>
                                    )}
                                </Box>

                            );
                        })}
                        <Box 
                            sx={{ 
                                position: 'absolute', 
                                top: '40px', 
                                left: 0, 
                                right: 0, 
                                bottom: 0, 
                                pointerEvents: 'none', 
                                zIndex: 2, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '4px',
                                
                                // 🚀 [수정 핵심 1]: 확장 상태(isCalFull)이거나, 또는 주소창이 세일즈 메뉴일 때 높이 제한과 잘림을 완벽하게 풀어줍니다.
                                height: (isCalFull || window.location.pathname === '/salesActivity') ? 'auto' : '100%',
                                overflow: (isCalFull || window.location.pathname === '/salesActivity') ? 'visible' : 'hidden',
                                
                                '@media (max-width: 480px)': {
                                    gap: '2px !important'
                                }
                            }}
                        >
                            {timelineTracks.map((_, trackIdx) => {
                                // 🚀 [수정 핵심 2]: 세일즈 메뉴 주소이거나 대시보드 확장 상태일 때는 3줄짜리 가두리 차단 필터를 무력화하여 모든 데이터를 정상적으로 화면에 그립니다.
                                if (trackIdx >= maxVisibleCount && !isWeekRowExpanded && !(isCalFull || window.location.pathname === '/salesActivity')) return null;
                                const currentTrackEvents = renderedWeekEvents.filter(e => e.topLevel === trackIdx);

                                return (
                                    <Box 
                                        key={trackIdx} 
                                        sx={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: `repeat(${weekendsVisible ? 7 : 5}, 1fr)`, 
                                            gap: '1px', 
                                            width: '100%', 
                                            height: '24px',
                                            position: 'relative',
                                            
                                            '@media (max-width: 480px)': {
                                                height: '20px !important'
                                            }
                                        }}
                                    >
                                        {currentTrackEvents.map((event) => {
                                            const formatToKorMD = (dateStr) => {
                                                if (!dateStr) return '';
                                                const [_, month, day] = dateStr.substring(0, 10).split('-');
                                                return `${Number(month)}월 ${Number(day)}일`;
                                            };
                                            
                                            const [startDate, startTimeFull] = event.start ? event.start.split(' ') : ['', ''];
                                            const startTime = startTimeFull ? startTimeFull.substring(0, 5) : '';
                                            const startMD = formatToKorMD(startDate);

                                            const [endDate, endTimeFull] = event.end ? event.end.split(' ') : ['', ''];
                                            const endTime = endTimeFull ? endTimeFull.substring(0, 5) : '';
                                            const endMD = formatToKorMD(endDate);

                                            let dateTimeRange = '';
                                            if (startMD) {
                                                if (endTime && startDate !== endDate) {
                                                    dateTimeRange = `(${startMD} ${startTime}~${endMD} ${endTime}) `;
                                                } else if (startTime && endTime) {
                                                    dateTimeRange = `(${startMD} ${startTime}~${endTime}) `;
                                                } else if (startTime) {
                                                    dateTimeRange = `(${startMD} ${startTime}) `;
                                                } else {
                                                    dateTimeRange = `(${startMD}) `;
                                                }
                                            }
                                            
                                            const storageUser = JSON.parse(localStorage.getItem('user'));                                            
                                            const myUuid = storageUser?.userUuid || storageUser?.usrUuid || '';
                                            const eventUsrUuid = event.usrUuid || event.extendedProps?.usrUuid || '';

                                            // 🚀 [수정 핵심 1]: ERP용으로 선별된 20가지 고급 비즈니스 컬러 세트 정의
                                            const managerColors = [
                                                '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#6d4c41', 
                                                '#455a64', '#00796b', '#c62828', '#ad1457', '#6a1b9a', 
                                                '#283593', '#1565c0', '#0288d1', '#558b2f', '#f9a825', 
                                                '#ef6c00', '#d84315', '#4e342e', '#37474f', '#546e7a'
                                            ];

                                            // 내 일정은 항상 원래 지정된 선명한 파란색을 최우선으로 고정합니다.
                                            let finalBgColor = '#1976d2'; 

                                            if (myUuid !== eventUsrUuid) {
                                                if (eventUsrUuid) {
                                                    // 🚀 [수정 핵심 2]: 담당자 UUID 문자열을 고유 해시 숫자로 변환합니다.
                                                    let hash = 0;
                                                    for (let i = 0; i < eventUsrUuid.length; i++) {
                                                        hash = eventUsrUuid.charCodeAt(i) + ((hash << 5) - hash);
                                                    }
                                                    // 20가지 색상 범위를 벗어나지 않도록 나머지 연산(% 20)을 통해 안전하게 인덱스를 추출합니다.
                                                    const colorIdx = Math.abs(hash) % managerColors.length;
                                                    finalBgColor = managerColors[colorIdx];
                                                } else {
                                                    // 담당자 식별코드가 빈 값인 경우 기본 초록색 부여
                                                    finalBgColor = '#2e7d32';
                                                }
                                            }

                                            const isLocationValid = window.location.pathname === '/salesActivity';
                                            const eventUsrNm = event.usrNm || event.extendedProps?.usrNm || '';
                                            const prefixName = (!isLocationValid && eventUsrNm) ? `[${eventUsrNm}] ` : '';

                                            return (
                                                <Box 
                                                    key={event.id} 
                                                    onClick={(e) => handleEventClick && handleEventClick(event, e)} 
                                                    sx={{ 
                                                        gridColumnStart: event.startIdx + 1, 
                                                        gridColumnEnd: `span ${event.colSpan}`, 
                                                        height: '24px', 
                                                        backgroundColor: finalBgColor, // 💫 20개 중 매핑된 고유 색상 주입
                                                        color: '#ffffff', 
                                                        px: '8px', 
                                                        mx: '2px', 
                                                        borderRadius: '4px', 
                                                        fontSize: '12px', 
                                                        lineHeight: '24px', 
                                                        cursor: 'pointer', 
                                                        whiteSpace: 'nowrap', 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        pointerEvents: 'auto', 
                                                        zIndex: 2, 
                                                        boxSizing: 'border-box', 
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.12)', 
                                                        opacity: event.end && dayjs(event.end).isBefore(dayjs(), 'day') ? 0.45 : 1,
                                                        '@media (max-width: 480px)': {
                                                            fontSize: '8px !important',
                                                            px: '2px !important',
                                                            mx: '1px !important',
                                                            height: '20px !important',
                                                            lineHeight: '20px !important'
                                                        },
                                                        '&:hover': { 
                                                            zIndex: 10, 
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                                                            opacity: 1
                                                        } 
                                                    }} 
                                                    title={`${prefixName}${event.title} ${dateTimeRange}`}
                                                >
                                                    {prefixName}{event.title} {dateTimeRange}
                                                </Box>
                                            );
                                        })}

                                    </Box>

                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default CalendarGrid;
